import React, {CSSProperties, useCallback, useLayoutEffect, useRef, useState} from "react";
import styles from "./consentDocumentPreview.module.scss";

/** A4 width in CSS pixels; documents are authored and exported for this page width. */
export const DOCUMENT_PAGE_WIDTH = 794;
const MAX_DOCUMENT_SCALE = 1.6;

export const documentFrameLayout = (width: number, height: number) => {
  const scale = width > DOCUMENT_PAGE_WIDTH ? Math.min(width / DOCUMENT_PAGE_WIDTH, MAX_DOCUMENT_SCALE) : 1;
  const frameWidth = scale > 1 ? DOCUMENT_PAGE_WIDTH : width;
  return {
    scale,
    width: frameWidth,
    height: height / scale,
    left: Math.max(0, Math.round((width - frameWidth * scale) / 2)),
  };
};

export interface ScaledDocumentFrameProps {
  srcDoc?: string;
  title: string;
  onLoad?: () => void;
}

/**
 * Lays an HTML document out at A4 width and scales it to the available width,
 * so any template reads at a comfortable size on large screens. Narrower
 * screens keep scale 1 and let the document reflow.
 *
 * The iframe is sized to its content and the surrounding viewport scrolls:
 * iOS Safari does not reliably touch-scroll inside an iframe placed in a
 * fixed overlay. `allow-same-origin` is only used to measure the content
 * height; scripts stay disabled by the sandbox.
 */
export const ScaledDocumentFrame = ({srcDoc, title, onLoad}: ScaledDocumentFrameProps) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [size, setSize] = useState({width: 0, height: 0});
  const [contentHeight, setContentHeight] = useState(0);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const measure = () => setSize({width: viewport.clientWidth, height: viewport.clientHeight});
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  const measureContent = useCallback(() => {
    const frame = frameRef.current;
    const content = frame?.contentDocument?.documentElement;
    if (!frame || !content) return;
    const previousHeight = frame.style.height;
    frame.style.height = "0px";
    const height = Math.max(content.scrollHeight, frame.contentDocument?.body?.scrollHeight || 0);
    frame.style.height = previousHeight;
    setContentHeight(height);
  }, []);

  const layout = size.width ? documentFrameLayout(size.width, size.height) : undefined;

  useLayoutEffect(() => {
    measureContent();
  }, [layout?.width, measureContent]);

  const frameHeight = layout ? Math.max(contentHeight, layout.height) : undefined;
  const frameStyle: CSSProperties | undefined = layout && frameHeight !== undefined ? {
    width: layout.width,
    height: frameHeight,
    left: layout.left,
    transform: layout.scale === 1 ? undefined : `scale(${layout.scale})`,
  } : undefined;
  const sizerStyle: CSSProperties | undefined = layout && frameHeight !== undefined
    ? {height: frameHeight * layout.scale}
    : undefined;

  return <div ref={viewportRef} className={styles.viewport}>
    <div className={styles.frameSizer} style={sizerStyle}>
      <iframe
        ref={frameRef}
        className={styles.scaledFrame}
        style={frameStyle}
        srcDoc={srcDoc}
        sandbox="allow-same-origin"
        scrolling="no"
        title={title}
        onLoad={() => {
          measureContent();
          onLoad?.();
        }}
      />
    </div>
  </div>;
};
